import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { TripCtx } from "./TripLayout";
import { Phone, Mail, Globe, Instagram, FileText } from "lucide-react";

export default function ExperiencesPage() {
  const { tripId } = useParams();
  const { API, authHeaders } = useAuth();
  const { trip } = useContext(TripCtx);
  const [experiences, setExperiences] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/trips/${tripId}/experiences`, { headers: authHeaders }).then((r) => r.data).catch(() => []),
      axios.get(`${API}/trips/${tripId}/addons`, { headers: authHeaders }).then((r) => r.data).catch(() => []),
    ]).then(([e, a]) => {
      setExperiences(e || []);
      setAddons(a || []);
      setLoading(false);
    });
  }, [tripId]);

  const fmtCur = (c, n) => n ? `${c || trip?.currency || "EUR"} ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : null;

  if (loading) return <p className="p-10 tcm-eyebrow">Loading experiences…</p>;

  const nothing = experiences.length === 0 && addons.length === 0;

  return (
    <div data-testid="client-experiences-page" className="py-4">
      <p className="tcm-eyebrow">Private productions</p>
      <h1 className="font-display text-4xl mt-2">Experiences</h1>
      <hr className="tcm-gold-rule w-16 my-6" />
      <p className="text-[13px] text-[color:var(--tcm-navy-70)] mb-8 max-w-xl">
        Everything we're staging around your trip — the dinners, the shoots, the florists, the private moments.
        Here are the vendors on your team and when to expect them.
      </p>

      {nothing && (
        <div className="border border-[color:var(--tcm-navy-15)] p-8 text-center" data-testid="experiences-empty">
          <p className="font-display text-xl">Nothing scheduled yet.</p>
          <p className="text-[13px] mt-2 text-[color:var(--tcm-navy-70)]">
            As your concierge finalises sub-productions, they'll appear here with vendor contacts and timing.
          </p>
        </div>
      )}

      {experiences.length > 0 && (
        <section className="mb-10">
          <p className="tcm-eyebrow mb-4">Scheduled experiences</p>
          <div className="space-y-6">
            {experiences.map((e) => (
              <article key={e.id} data-testid={`client-exp-${e.id}`}
                       className="border border-[color:var(--tcm-navy)] p-5">
                <p className="tcm-eyebrow" style={{ color: "var(--tcm-gold-700)" }}>
                  {e.date || "Date to be confirmed"}
                </p>
                <h3 className="font-display text-[22px] mt-1">{e.title}</h3>
                {e.notes && (
                  <p className="text-[13px] mt-2 text-[color:var(--tcm-navy-70)] whitespace-pre-wrap">
                    {e.notes}
                  </p>
                )}
                {(e.vendors || []).length > 0 && (
                  <>
                    <hr className="tcm-gold-rule w-10 my-4" />
                    <p className="tcm-eyebrow mb-3">Your team</p>
                    <ul className="space-y-3">
                      {e.vendors.map((v) => (
                        <li key={v.id} data-testid={`client-vendor-${v.id}`}
                            className="border border-[color:var(--tcm-navy-15)] p-3">
                          <div className="flex justify-between items-baseline gap-3 flex-wrap">
                            <div>
                              <p className="font-display text-[17px]">{v.name}</p>
                              <p className="tcm-eyebrow">{v.role || "vendor"}</p>
                            </div>
                            {fmtCur(v.currency, v.cost) && (
                              <span className="text-[12px] tracking-[0.1em] uppercase tabular-nums">
                                {fmtCur(v.currency, v.cost)}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 mt-3 text-[12px]">
                            {v.phone && (
                              <a href={`tel:${v.phone}`}
                                 className="inline-flex items-center gap-1.5 tcm-eyebrow underline decoration-[#C9A961] underline-offset-4"
                                 data-testid={`client-vendor-phone-${v.id}`}>
                                <Phone size={12} /> Call
                              </a>
                            )}
                            {v.email && (
                              <a href={`mailto:${v.email}`}
                                 className="inline-flex items-center gap-1.5 tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
                                <Mail size={12} /> Email
                              </a>
                            )}
                            {v.website && (
                              <a href={v.website} target="_blank" rel="noreferrer"
                                 className="inline-flex items-center gap-1.5 tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
                                <Globe size={12} /> Website
                              </a>
                            )}
                            {v.instagram && (
                              <a href={`https://instagram.com/${v.instagram.replace(/^@/, "")}`}
                                 target="_blank" rel="noreferrer"
                                 className="inline-flex items-center gap-1.5 tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
                                <Instagram size={12} /> @{v.instagram.replace(/^@/, "")}
                              </a>
                            )}
                            {v.brochure_url && (
                              <a href={v.brochure_url} target="_blank" rel="noreferrer"
                                 className="inline-flex items-center gap-1.5 tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
                                <FileText size={12} /> Brochure
                              </a>
                            )}
                          </div>
                          {v.notes && (
                            <p className="text-[12px] mt-2 text-[color:var(--tcm-navy-70)] whitespace-pre-wrap">
                              {v.notes}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {addons.length > 0 && (
        <section className="mb-10">
          <p className="tcm-eyebrow mb-4">Bespoke add-ons</p>
          <div className="space-y-4">
            {addons.map((a) => (
              <article key={a.id} data-testid={`client-addon-${a.id}`}
                       className="border border-[color:var(--tcm-navy-15)] p-4">
                <div className="flex justify-between items-baseline gap-3 flex-wrap">
                  <div>
                    <p className="tcm-eyebrow" style={{ color: "var(--tcm-gold-700)" }}>
                      {a.category || "Add-on"}
                    </p>
                    <p className="font-display text-[18px] mt-0.5">{a.title}</p>
                    {a.supplier_name && (
                      <p className="text-[12px] mt-0.5 text-[color:var(--tcm-navy-70)]">
                        with {a.supplier_name}
                      </p>
                    )}
                  </div>
                  {fmtCur(a.currency, a.amount) && (
                    <span className="text-[12px] tracking-[0.1em] uppercase tabular-nums">
                      {fmtCur(a.currency, a.amount)}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 mt-3 text-[12px]">
                  {a.website && (
                    <a href={a.website} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
                      <Globe size={12} /> Website
                    </a>
                  )}
                  {a.instagram && (
                    <a href={`https://instagram.com/${a.instagram.replace(/^@/, "")}`}
                       target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
                      <Instagram size={12} /> @{a.instagram.replace(/^@/, "")}
                    </a>
                  )}
                  {a.brochure_url && (
                    <a href={a.brochure_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
                      <FileText size={12} /> Brochure
                    </a>
                  )}
                </div>
                {a.notes && (
                  <p className="text-[12px] mt-2 text-[color:var(--tcm-navy-70)] whitespace-pre-wrap">
                    {a.notes}
                  </p>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
